import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { concatLatestFrom } from '@ngrx/operators';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { TranslateService } from '@ngx-translate/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { of } from 'rxjs';
import { catchError, filter, map, mergeMap, tap } from 'rxjs/operators';
import { AppLockComponent } from '../../../services/date-manipulation-check/app-lock.component';
import { AppLockModel } from '../../../services/date-manipulation-check/app-lock.model';
import { UriUtilitiesService } from '../../../services/uri-utilities/uri-utilities.service';
import { ENDPOINTS } from '../../../enums/endpoints.enum';
import { ODataIsAliveResponse } from '../../../interfaces/is-alive-set.interface';
import { hydrateSlice, persistenceError, persistSlice, sliceHydrated, slicePersisted } from '../../persistence';
import {
    dateManipulationCheckFailed,
    dateManipulationCheckLoaded,
    dateManipulationCheckTimestampsRestoredFromStorage,
    externalTimestampPersisted,
    externalTimestampPersistError,
    externalTimestampReceived,
    initDateManipulationCheck,
    initialTimestampCheckRequested,
    timestampCheckRequested,
} from '../actions/date-manipulation-check.actions';
import { isAliveResponseToTimestampState } from '../date-manipulation-check.converters';
import { DATE_MANIPULATION_CHECK_FEATURE_KEY, PersistedTimestamps } from '../date-manipulation-check-collection.state';
import {
    selectDateManipulationCheckLoaded,
    selectLastTimestampCheck,
    selectLastTimestampCheckValid,
} from '../selectors/date-manipulation-check.selector';

/**
 * Owns the date-manipulation-check slice's init chain, its `IsaliveSet` fetch, and the side
 * effects of a validity check (the blocking modal, its debug logs, and mirroring the internal
 * timestamp to storage on success).
 *
 * Reducers run synchronously inside `dispatch()`, so every `concatLatestFrom` below observes
 * **post-reducer** state for the action that triggered it — e.g. `appLock$` reading
 * `selectLastTimestampCheckValid` after `timestampCheckRequested` sees the outcome
 * `dateManipulationCheckTimestampReducer` just computed, not the previous one.
 */
@Injectable()
export class DateManipulationCheckEffects {
    /**
     * The currently displayed app-lock modal, if any. An effect field rather than store state:
     * a `BsModalRef` wraps a live component instance and is not serializable, so it cannot live
     * in the store (see `EscalationEffects`, `NotificationEffects`, `UserMasterEffects` for the
     * same pattern).
     */
    private modalInstance: BsModalRef | undefined;

    public constructor(
        private readonly actions$: Actions,
        private readonly store: Store,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly httpClient: HttpClient,
        private readonly uriUtilitiesService: UriUtilitiesService,
        private readonly modalService: BsModalService,
        private readonly translate: TranslateService,
    ) {}

    /**
     * Trigger rehydration from storage on init, unless the slice is already hydrated.
     *
     * The readiness flag is sampled once per action with `concatLatestFrom` rather than
     * `switchMap`-ing into `store.select(...)`: that would leave a live inner subscription behind
     * which re-emits whenever `loadStatus` leaves `Loaded`. `AppInitializationGuard.canActivate`
     * calls `checkInit()` again after a failed startup, which re-runs `init()`, so a second
     * `initDateManipulationCheck` is reachable — and with a lingering subscription it would
     * hydrate (and re-fetch `IsaliveSet`) once per previous init instead of once.
     *
     * A re-init still re-runs the whole chain, because `dateManipulationCheckInitReducer` resets
     * `loadStatus` to `Loading` before any effect sees the action.
     */
    public hydrateOnInit$ = createEffect(() =>
        this.actions$.pipe(
            ofType(initDateManipulationCheck),
            concatLatestFrom(() => this.store.select(selectDateManipulationCheckLoaded)),
            filter(([, loaded]) => !loaded),
            map(() => hydrateSlice({ featureKey: DATE_MANIPULATION_CHECK_FEATURE_KEY })),
        ),
    );

    /** Bridge the generic hydrate result into the slice, unless already hydrated. */
    public applyHydrated$ = createEffect(() =>
        this.actions$.pipe(
            ofType(sliceHydrated),
            filter(({ featureKey }) => featureKey === DATE_MANIPULATION_CHECK_FEATURE_KEY),
            concatLatestFrom(() => this.store.select(selectDateManipulationCheckLoaded)),
            filter(([, loaded]) => !loaded),
            map(([{ data }]) =>
                dateManipulationCheckTimestampsRestoredFromStorage({
                    timestamps: (data as PersistedTimestamps | undefined) ?? {
                        lastValidInternalTimestamp: '',
                        lastValidExternalTimestamp: '',
                    },
                }),
            ),
        ),
    );

    /**
     * Fetches the external (gateway) timestamp once the persisted timestamps are restored, then
     * emits both `externalTimestampReceived` and `initialTimestampCheckRequested` as a pair — in
     * that order — so the external timestamp always lands in the slice before the init check
     * runs against it. `catchError` maps a failed fetch to `dateManipulationCheckFailed`.
     */
    public fetchExternalTimestamp$ = createEffect(() =>
        this.actions$.pipe(
            ofType(dateManipulationCheckTimestampsRestoredFromStorage),
            mergeMap(() =>
                this.httpClient
                    .get<ODataIsAliveResponse>(
                        this.uriUtilitiesService.getUriForContent() + ENDPOINTS.IsAlive + '?$format=json',
                    )
                    .pipe(
                        mergeMap((response) => [
                            externalTimestampReceived({ timestamp: isAliveResponseToTimestampState(response) }),
                            initialTimestampCheckRequested({ timestamp: new Date().toISOString() }),
                        ]),
                        catchError((error) => of(dateManipulationCheckFailed({ error: String(error) }))),
                    ),
            ),
        ),
    );

    /**
     * Marks the slice loaded once the init check has run — regardless of its outcome. This
     * reproduces the legacy service exactly: `dateManipulationCheck()` returns `throwError(...)`
     * from inside a `map`, so the error is never subscribed and `init()` never actually fails on
     * an invalid date. The app-lock modal still appears (see `appLock$`); only initialization
     * itself is unaffected. Fixing that swallowed error is tracked in
     * `docs/tickets/init-invalid-date-error-swallowed.md`, deliberately out of scope for this
     * migration so turning the flag on stays behavior-neutral.
     */
    public completeInit$ = createEffect(() =>
        this.actions$.pipe(
            ofType(initialTimestampCheckRequested),
            map(() => dateManipulationCheckLoaded()),
        ),
    );

    /** Reproduces the legacy `checkAndUpdateLastValidTimestamp` debug log for every check. */
    public reportCheck$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(timestampCheckRequested, initialTimestampCheckRequested),
                concatLatestFrom(() => this.store.select(selectLastTimestampCheck)),
                tap(([, check]) => {
                    this.log.debug(
                        `DateManipulationCheckEffects: reportCheck$: Current system time: ${new Date().toISOString()}, time to check: ${
                            check.checkedTimestamp
                        }, last valid internal timestamp: ${check.previousInternalTimestamp}, last valid external timestamp: ${
                            check.previousExternalTimestamp
                        }`,
                    );
                }),
            ),
        { dispatch: false },
    );

    /**
     * Hides any previously displayed app-lock modal on **every** check (matching the legacy
     * `checkTimestamp`'s unconditional `this.modalInstance?.hide()`), then shows a fresh one when
     * the check failed.
     */
    public appLock$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(timestampCheckRequested, initialTimestampCheckRequested),
                concatLatestFrom(() => this.store.select(selectLastTimestampCheckValid)),
                tap(([, valid]) => {
                    this.modalInstance?.hide();
                    this.modalInstance = undefined;

                    if (valid) {
                        return;
                    }

                    this.log.debug(
                        `DateManipulationCheckEffects: appLock$: Mismatching timestamps between system and external dates, blocking application`,
                    );

                    const titleText = <string>this.translate.instant('APP_DATE_TIME_ERROR_POPUP_TITLE');
                    const bodyText = `${<string>this.translate.instant('APP_DATE_TIME_ERROR_POPUP_BODY')} <br><br><b>${<
                        string
                    >this.translate.instant('APP_DATE_TIME_SETTINGS_PATH')}</b>`;

                    this.modalInstance = this.modalService.show(AppLockComponent, {
                        backdrop: 'static',
                        class: 'modal-md',
                        initialState: {
                            model: new AppLockModel(titleText, bodyText),
                        },
                        ariaLabelledBy: 'app-lock-modal-title',
                    });
                }),
            ),
        { dispatch: false },
    );

    /** Legacy only writes the internal timestamp to storage on a successful check. */
    public persistOnValidCheck$ = createEffect(() =>
        this.actions$.pipe(
            ofType(timestampCheckRequested, initialTimestampCheckRequested),
            concatLatestFrom(() => this.store.select(selectLastTimestampCheckValid)),
            filter(([, valid]) => valid),
            map(() => persistSlice({ featureKey: DATE_MANIPULATION_CHECK_FEATURE_KEY })),
        ),
    );

    /** Mirror a freshly received external timestamp to storage via the generic layer. */
    public persistExternalTimestamp$ = createEffect(() =>
        this.actions$.pipe(
            ofType(externalTimestampReceived),
            map(({ correlationId }) =>
                persistSlice({ featureKey: DATE_MANIPULATION_CHECK_FEATURE_KEY, correlationId }),
            ),
        ),
    );

    /** Bridge the generic persist success back to the external-timestamp settle action. */
    public settleExternalTimestampWrite$ = createEffect(() =>
        this.actions$.pipe(
            ofType(slicePersisted),
            filter(({ featureKey }) => featureKey === DATE_MANIPULATION_CHECK_FEATURE_KEY),
            map(({ correlationId }) => externalTimestampPersisted({ correlationId })),
        ),
    );

    /** Bridge the generic persist failure back to the external-timestamp settle action. */
    public settleExternalTimestampWriteError$ = createEffect(() =>
        this.actions$.pipe(
            ofType(persistenceError),
            filter(({ featureKey }) => featureKey === DATE_MANIPULATION_CHECK_FEATURE_KEY),
            map(({ error, correlationId }) => externalTimestampPersistError({ error, correlationId })),
        ),
    );
}
